import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

const isMobileDevice = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/** Shares a rendered clip: the phone's share sheet with the video file, or a
 *  QR code to the /share page elsewhere. `dialog` must be rendered by the
 *  caller (it is the QR code dialog). */
export function useShareClip({ downloadUrl, clipName }: { downloadUrl: string; clipName: string }): {
  share: () => Promise<void>;
  isSharing: boolean;
  dialog: React.ReactNode;
} {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const share = async () => {
    if (isMobileDevice() && navigator.share) {
      setIsSharing(true);
      try {
        const response = await axios.get(downloadUrl, {
          responseType: "blob",
        });

        const urlPath = new URL(downloadUrl, window.location.href).pathname;
        const ext = (urlPath.match(/\.([a-zA-Z0-9]+)$/)?.[1] || "mp4").toLowerCase();
        const safeName = (clipName || "clip").replace(/[^a-zA-Z0-9-_.]+/g, "_");
        const fileName = `${safeName}.${ext}`;

        const file = new File([response.data], fileName, {
          type: response.data.type || "video/mp4",
        });

        await navigator.share({
          files: [file],
          title: clipName,
        });
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("Share failed:", error);
          toast({
            title: t("shareClip.shareFailed"),
            variant: "destructive",
          });
        }
      } finally {
        setIsSharing(false);
      }
    } else {
      setQrDialogOpen(true);
    }
  };

  const sharePageUrl = `${window.location.origin}/share?url=${encodeURIComponent(downloadUrl)}&name=${encodeURIComponent(clipName)}`;

  const dialog = (
    <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
      <DialogContent className="max-w-sm" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>{t("shareClip.scanToShare")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4 py-4">
          <p className="text-sm text-muted-foreground text-center">
            {t("shareClip.scanQrCode")}
          </p>
          <div className="bg-white p-4 rounded-lg">
            <QRCodeSVG
              value={sharePageUrl}
              size={200}
              level="M"
            />
          </div>
          <p className="text-xs text-muted-foreground text-center truncate max-w-full">
            {clipName}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );

  return { share, isSharing, dialog };
}
